import { FormGroup, FormText, Label } from "reactstrap";
import CreatableSelect from "react-select/creatable";
import { useState } from "react";
import { MultiValue } from "react-select";
import { useTagsQuery } from "../../crew/useTagsQuery";
import { useMutation } from "urql";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useMutationSaveStatus } from "./useMutationSaveStatus";
import { SaveStatusIndicator } from "./SaveStatusIndicator";

export type ValueAndLabel = { value: string; label: string };

interface Props<
  TData = unknown,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
> {
  labelValue: string;
  htmlTagIdAndName: string;
  initialValues: readonly string[];
  bucket: string;
  mutationDocument: TypedDocumentNode<TData, TVariables>;
  buildVariables: (values: readonly string[]) => TVariables;
  placeholder?: string;
  helpText?: string;
  isDisabled?: boolean;
}

/**
 * Creatable multi-select component with GraphQL mutation support.
 *
 * This component provides a searchable dropdown with tag creation capabilities
 * and automatic mutation on change. Based on react-select/creatable with GraphQL
 * integration similar to SelectGraphQlMutate.
 *
 * @example
 * <CreatableSelectGraphQlMutate
 *     labelValue="Tags"
 *     htmlTagIdAndName="tags"
 *     initialValues={song.tags}
 *     bucket={song.bucket}
 *     mutationDocument={updateTagsMutation}
 *     buildVariables={(values) => ({
 *         id: song.id,
 *         tags: values,
 *         bucket: song.bucket
 *     })}
 *     placeholder="Tags auswählen oder erstellen..."
 *     helpText="Verwende Tags, um später nach allen Liedern mit diesem Tag zu suchen."
 * />
 */
export function CreatableSelectGraphQlMutate<
  TData = unknown,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
>({
  labelValue,
  htmlTagIdAndName,
  initialValues,
  bucket,
  mutationDocument,
  buildVariables,
  placeholder = `${labelValue} auswählen...`,
  helpText,
  isDisabled = false,
}: Props<TData, TVariables>) {
  const tagsQuery: ReadonlyArray<string> | undefined = useTagsQuery(bucket);
  const [, executeMutation] = useMutation(mutationDocument);
  const {
    status: saveStatus,
    markSaving,
    markSettled,
  } = useMutationSaveStatus();

  const [value, setValue] = useState<MultiValue<ValueAndLabel>>(
    initialValues.map((tag) => ({ value: tag, label: tag })),
  );

  const submit = (newValues: readonly string[]) => {
    const variables = buildVariables(newValues);
    markSaving();
    executeMutation(variables)
      .then((result) => {
        console.debug("Mutation result", result);
        markSettled(!!result.error);
      })
      .catch((err) => {
        console.error("Mutation threw", err);
        markSettled(true);
      });
  };

  const onChange = (newValue: MultiValue<ValueAndLabel>) => {
    setValue(newValue);
    const stringValues = newValue.map((v) => v.value);
    submit(stringValues);
  };

  const onCreateOption = (inputValue: string) => {
    const newOption: ValueAndLabel = { value: inputValue, label: inputValue };
    const newValue = [...value, newOption];
    setValue(newValue);
    const stringValues = newValue.map((v) => v.value);
    submit(stringValues);
  };

  return (
    <FormGroup>
      <Label htmlFor={htmlTagIdAndName} className="d-flex align-items-center">
        {labelValue}
        <SaveStatusIndicator
          htmlTagIdAndName={htmlTagIdAndName}
          status={saveStatus}
        />
      </Label>

      <CreatableSelect<ValueAndLabel, true>
        isMulti
        id={htmlTagIdAndName}
        name={htmlTagIdAndName}
        value={value}
        onChange={onChange}
        onCreateOption={onCreateOption}
        options={tagsQuery?.map((tag) => ({ value: tag, label: tag })) ?? []}
        placeholder={placeholder}
        noOptionsMessage={() => "Keine Tags gefunden"}
        isDisabled={isDisabled}
      />

      {helpText && <FormText>{helpText}</FormText>}
    </FormGroup>
  );
}
